import { useState, type ReactNode } from "react";
import { EnvironmentBadge } from "../Environment/EnvironmentBadge";
import {
  DevMenuRow,
  DevSectionHeading,
  type DevMenuTone,
} from "./DevMenuPrimitives";
import { InspectorKeyRow, InspectorToggleRow } from "./InspectorMenuItems";
import { ThemeMenuItem } from "./ThemeMenuItem";
import { ViewportRow } from "./ViewportRow";

interface DevMenuProps {
  /**
   * Master gate. When false the entire menu (and its keyboard shortcuts)
   * renders nothing — no DOM trace. Each app supplies its own predicate:
   * dashboard uses employee-email + dev-mode, carriers uses non-prod, hubble
   * mounts it for everyone (it's employee-only via MSAL).
   */
  enabled: boolean;
  /**
   * Visual tone. Default `"hubble"` (compact). Pass `"cargo"` in dashboard so
   * the rows match the surrounding Cargo MenuItem styling.
   */
  tone?: DevMenuTone | undefined;

  /** Environment badge — shown when an environment name is provided. */
  environment?: string | undefined;
  isLocalOverride?: boolean | undefined;

  /**
   * Developer Settings section (Inspector + app extras) — the engineering
   * tools. Default true. Hubble sets this to `isEngineeringTeamMember` so
   * non-engineers still get the Display section but not the eng toggles.
   */
  showDeveloperSettings?: boolean | undefined;
  /** Show the inspector toggle + activation-key rows. Default true. */
  showInspector?: boolean | undefined;
  /**
   * Extra rows rendered inside Developer Settings after the inspector rows —
   * e.g. Mock Data and Cold Start Overlay toggles.
   */
  extraDevRows?: ReactNode;

  /** Display section — Viewport read-out. Default true. */
  showViewport?: boolean | undefined;
  /** Display section — Theme cycle. Requires <ThemeProvider> in tree. Default true. */
  showTheme?: boolean | undefined;
}

/**
 * The shared dev menu section, embedded inside each app's user menu. Renders up
 * to three groups: the API Environment badge, a collapsible Developer Settings
 * group (inspector + consumer extras), and a Display group (Viewport + Theme).
 * Every app composes the same component so the developer surface stays
 * identical across dashboard, hubble, and carriers.
 *
 * Returns `null` when `enabled` is false — the whole affordance is gone.
 */
// oxlint-disable-next-line complexity -- grandfathered, see ENG-7672
export function DevMenu({
  enabled,
  tone = "hubble",
  environment,
  isLocalOverride,
  showDeveloperSettings = true,
  showInspector = true,
  extraDevRows,
  showViewport = true,
  showTheme = true,
}: DevMenuProps) {
  const [open, setOpen] = useState(false);

  if (!enabled) return null;

  const showDisplay = showViewport || showTheme;

  return (
    <>
      {environment && (
        <EnvironmentBadge
          environment={environment}
          isLocalOverride={isLocalOverride}
          tone={tone}
        />
      )}

      {showDeveloperSettings && (
        <>
          <DevMenuRow onClick={() => setOpen((v) => !v)} tone={tone}>
            <span>Developer Settings</span>
            {/* Inline SVG (not a text glyph) so the row's textContent stays
                exactly "Developer Settings" for tests + screen readers. */}
            <svg
              aria-hidden
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-90" : ""}`}
            >
              <path d="M6 4l4 4-4 4" />
            </svg>
          </DevMenuRow>
          {open && (
            <div className="bg-card">
              {showInspector && <InspectorToggleRow indented tone={tone} />}
              {showInspector && <InspectorKeyRow indented tone={tone} />}
              {extraDevRows}
            </div>
          )}
        </>
      )}

      {showDisplay && (
        <>
          <DevSectionHeading tone={tone}>Display</DevSectionHeading>
          {showViewport && <ViewportRow tone={tone} />}
          {showTheme && <ThemeMenuItem tone={tone} />}
        </>
      )}
    </>
  );
}
